import type { AllergensCopy } from '../types/allergens';

export const allergensEn: AllergensCopy = {
  seo: {
    title: 'EU Allergens: The 14 and the Law, Explained',
    description:
      'The 14 EU allergens and what the law asks of restaurants, in plain language. Learn how to declare allergens on your menu, plus the hidden sources that catch kitchens out.',
  },
  hero: {
    eyebrow: 'Field guide for restaurant owners',
    headingPre: 'The 14 EU allergens, explained in',
    headingScribble: 'plain language',
    body: 'Know which allergens the law covers, where they hide, and how to tell your guests clearly. This guide walks you through both the dishes and the rules, with no legalese.',
    factAllergenCount: '14 allergens',
    factRegulation: 'Regulation (EU) No 1169/2011',
    factReadTime: '6 min read',
    imageAlt: 'A restaurant table with a SafestMenu QR menu',
  },
  intro: {
    leadPre: 'If you serve food in the EU, naming these 14 allergens is both',
    leadScribble1: 'legally required',
    leadMid: 'and',
    leadScribble2: 'part of good hospitality',
    leadPost: '.',
    body: 'These allergens hide in more places than most menus admit. This page explains the 14, the law behind them, and how to declare them clearly - plain and practical, so you can act on it today.',
    stats: [
      { big: '14', small: 'substances on one fixed list' },
      { big: 'Every', small: 'EU restaurant, cafe and bar' },
      { big: 'Named', small: 'clearly, never left to chance' },
    ],
  },
  whyItMatters: {
    eyebrow: 'Why it matters',
    heading: 'Safe guests are the ones who come back.',
    imageAlt: 'Guests looking at a menu together at a restaurant table',
    cards: [
      {
        title: 'A reaction can be serious',
        text: 'For some guests, a hidden trace is not a preference - it can send them to hospital.',
      },
      {
        title: 'Trust is hospitality',
        text: 'Get it right and people relax. Clear allergen info is part of a warm welcome, not red tape.',
      },
      {
        title: 'It is the law',
        text: 'The restaurant is the responsible food business operator, so the duty sits with you, not your supplier.',
      },
    ],
  },
  theFourteen: {
    eyebrow: 'The reference',
    heading: 'Meet the fourteen',
    intro: 'These are the allergens named in Annex II of Regulation (EU) No 1169/2011. It is a fixed, closed list - you do not choose which to track, and every dish that contains one has to say so.',
    gotchas: {
      heading: '3 that catch kitchens out',
      items: [
        'Peanut oil always counts as peanut, refined or not. Only fully refined soybean oil is exempt, so never extend that to peanut, groundnut or arachis oil.',
        'Oats are naturally gluten-free but almost always cross-contaminated in milling. Treat plain oats as gluten unless they are certified gluten-free.',
        'Soy lecithin (E322) sits in a huge share of chocolate, spreads and baked goods, so check supplier sheets, not just the visible ingredients.',
      ],
    },
  },
  legislation: {
    eyebrow: 'The law, briefly',
    heading: 'Regulation (EU) No 1169/2011',
    paragraphs: [
      'The rules come from Regulation (EU) No 1169/2011, often called the Food Information to Consumers Regulation, or FIC. As an EU Regulation it applies directly in every member state, including Romania, with no local version needed.',
      'It has applied since 13 December 2014. Article 9 lists the information food must carry, Article 21 covers how allergens are emphasised on packaged food, and Annex II is the list of the 14 allergens themselves.',
      "For food served loose - the meals you cook to order - Article 44 makes allergen information mandatory but leaves the method to each country. Penalties are set nationally too, under each country's own food-safety law, not by the Regulation itself.",
    ],
    asksHeading: 'What it asks of you',
    asksItems: [
      'Names 14 allergens you must declare',
      'Applies to both menus and labels',
      'Covers prepacked and loose, served food',
      'Must be accurate and kept up to date',
    ],
    natashasLaw:
      "One you may have heard of, the UK's Natasha's Law, is a UK rule from 2021 - stricter than the EU baseline, and not part of EU law at all.",
  },
  steps: {
    eyebrow: 'How to declare it',
    heading: 'Turn the duty into a simple routine',
    intro: 'These 4 steps take you from a pile of recipes to allergen information a guest can trust.',
    items: [
      {
        title: 'Build one source of truth',
        text: 'Mark which of the 14 each dish contains, pulled from your recipes and supplier specs. Keep it in one place so kitchen and floor always agree.',
      },
      {
        title: 'Put it where guests see it',
        text: 'Show it in writing by default: on the menu, on a clear chart, or on a digital menu. Written beats relying on someone to remember.',
      },
      {
        title: 'Train your team',
        text: 'Front-of-house should read from the record and know when to check with the kitchen rather than guess. Accurate answers, every time.',
      },
      {
        title: 'Keep it current',
        text: 'Every recipe tweak or new supplier can add or remove an allergen. Update the record the moment something changes.',
      },
    ],
    footnote: 'Re-check whenever a recipe or a supplier changes - that is where most menus quietly go out of date.',
  },
  comparison: {
    eyebrow: 'Where the info goes',
    heading: 'Prepacked vs loose food',
    intro: 'The same 14 allergens, declared in different places depending on how the food is sold. This is the mix-up we see most.',
    leftTitle: 'Prepacked, packaged food',
    leftPoints: [
      'Sold in the packaging it was put in before being ordered, like a bottled sauce.',
      'Needs a full ingredients list, with the 14 allergens emphasised in it (Article 21).',
      'Also carries the other label details: name, quantity and durability date.',
    ],
    rightTitle: 'Food served loose',
    rightPoints: [
      'Meals you cook to order and serve, plus food packed on site when a guest asks.',
      'No printed ingredients list required, but allergen information is mandatory (Article 44).',
      'Given the way your country allows, and always accurate and easy to get before ordering.',
    ],
  },
  crossContact: {
    eyebrow: 'In the kitchen',
    heading: 'Cross-contact and “may contain”',
    imageAlt: 'A clean, freshly wiped restaurant prep surface',
    bodyPre:
      'Declaring allergens that are ingredients is one job. Cross-contact is another - an allergen moving by accident through shared fryers, boards or knives. A',
    bodyScribble: 'trace is enough',
    bodyPost:
      ', so “may contain” is only honest after you have looked at the real risk and cannot remove it.',
    tiles: [
      'Separate prep and utensils',
      'Clean down between orders',
      'Train the whole team',
      'Be honest with may-contain',
    ],
  },
  digital: {
    eyebrow: 'Where a QR menu helps',
    heading: 'One edit, current on every table',
    body: 'Printed menus and laminated charts share one weak spot: keeping them current. A digital menu removes that. You tag each dish once, and an edit reaches every guest straight away - no reprint.',
    list: [
      'Tag the 14 allergens per dish, once',
      'Guests filter the menu to what is safe for them',
      'Always the current version, in their own language',
    ],
    footnote: 'Honest about the limits: a QR menu does not make your data correct or replace staff training. It fixes stale information, which is the most common thing that goes wrong.',
    ctaLabel: 'See how SafestMenu handles allergens',
    qrImageAlt: 'A SafestMenu QR code table card next to a coffee',
    dishImageAlt: 'A plated dish shown on a SafestMenu digital menu',
    journeyEyebrow: 'The allergen information journey',
    journeyIntro: 'Allergen info is a flow with 4 hand-offs. Your menu - and a tool like SafestMenu - sits at the point where it reaches the guest.',
  },
  faq: {
    eyebrow: 'Common questions',
    heading: 'Quick answers',
    items: [
      {
        q: 'How many allergens do I have to declare?',
        a: 'All 14. EU law sets a fixed list in Annex II of Regulation 1169/2011, and every dish that contains one has to say so. You can flag other things guests ask about, but the 14 are the legal minimum.',
      },
      {
        q: 'Can I just tell guests to ask a member of staff?',
        a: 'It depends on your country. Some member states allow allergen information on request, if a clear sign points guests to ask and staff give accurate answers. Others, like France, require it in writing. Writing it down is the safer default everywhere.',
      },
      {
        q: "Does Natasha's Law apply to my restaurant?",
        a: "Only if you are in the UK. Natasha's Law is a UK rule from 1 October 2021 for food packed on site before a guest orders it, and it is stricter than the EU baseline. It does not apply in Romania or the rest of the EU.",
      },
      {
        q: 'Is peanut oil exempt from being declared?',
        a: 'No. Peanut oil counts as peanut whether it is refined or not, so it always has to be declared. Only fully refined soybean oil has that exemption, and groundnut and arachis oil are just other names for peanut oil.',
      },
      {
        q: 'Who is responsible if the allergen information is wrong?',
        a: 'You are. The restaurant is the food business operator, so the duty to give accurate information sits with you, even if a supplier changed a recipe without telling you. That is why one current record matters.',
      },
      {
        q: 'Does a QR menu keep me compliant on its own?',
        a: 'No, and we would not claim it does. A digital menu keeps your allergen information current across every table at once, but the data is only as good as what you put in. You still need trained staff and a real cross-contact process in the kitchen.',
      },
    ],
  },
  closingNote: {
    heading: 'A note on this guide',
    body: 'This is general guidance to help you get organised, not legal advice. National rules under Article 44 differ across the EU and get updated, so check the rules where you operate, or ask your national food-safety authority.',
  },
  emailCapture: {
    heading: 'Keep this guide handy',
    title: 'Get the allergen checklist by email',
    description: 'We will send this guide plus the occasional practical note on menus and allergen labeling. No spam, unsubscribe any time.',
    buttonLabel: 'Send it to me',
  },
  cta: {
    heading: 'Put your allergens on a menu guests can trust',
    body: 'Tag the 14 allergens per dish, update once, and every guest sees the change at the table. You keep one record that stays current, and guests filter the menu to what is safe for them.',
    buttonLabel: 'Try SafestMenu free',
  },
  diagrams: {
    journey: [
      { title: 'The dish', sub: "What's really in it, down to the sauce" },
      { title: 'The record', sub: 'Each dish mapped to the 14' },
      { title: 'The menu', sub: 'Shown to the guest, on paper or screen', tag: 'SafestMenu' },
      { title: 'The guest', sub: 'Makes a choice they can trust' },
    ],
    routes: [{ label: 'On the menu' }, { label: 'On a sign' }, { label: 'Told on request' }],
    orLabel: 'or',
    ingredients: {
      label: 'Ingredients',
      segments: [
        { text: 'Wheat flour', mark: true },
        { text: ', water, rapeseed oil, ' },
        { text: 'egg', mark: true },
        { text: ', sugar, ' },
        { text: 'milk', mark: true },
        { text: ', salt, barley malt.' },
      ],
      note: 'Allergens emphasised inside the ingredients list.',
    },
  },
  reference: {
    ariaLabel: 'The 14 EU allergens',
    allergenLabel: 'Allergen',
    commonlyIn: 'Commonly in',
    hidesIn: 'Hides in',
    remember: 'Remember',
    footnotePre: 'Tap any of the ',
    footnotePost: ' to see where it hides. The examples are a starting point - always check your own recipes and supplier specs.',
  },
  items: {
    'cereals-gluten': {
      name: 'Cereals with gluten',
      short: 'Wheat, rye, barley, oats, spelt',
      common: 'Bread, pasta, pizza dough, cakes, pastry, batter, breadcrumbs, couscous, beer, and most flour-thickened sauces and gravies.',
      hidden: 'Soy sauce (usually wheat-fermented), stock cubes and gravy granules, malt vinegar, some sausages and ice creams (rusk or wheat filler), and spice blends.',
      note: 'Oats are gluten-free by nature but nearly always cross-contaminated in milling. Do not treat a plain oat dish as safe for coeliacs unless it is certified gluten-free.',
    },
    crustaceans: {
      name: 'Crustaceans',
      short: 'Prawns, crab, lobster, scampi',
      common: 'Prawns, shrimp, crab, lobster, langoustine, crayfish and scampi.',
      hidden: 'Shrimp paste (belacan) in Thai and South-East Asian curries, seafood stock in soups and risottos, bouillabaisse and paella bases, and prawn crackers.',
      note: 'A trace of prawn stock in a "vegetable" curry base is enough to trigger a severe reaction. Check shared woks, fryers and stockpots.',
    },
    eggs: {
      name: 'Eggs',
      short: 'Mayo, cakes, glazes, custard',
      common: 'Mayonnaise, quiche, omelettes, cakes, meringue, fresh egg pasta, custard, and battered or breaded foods.',
      hidden: 'Egg glaze brushed on pastry and bread, consommés fined with egg white, mousses, marshmallows, and foam toppings on cocktails.',
      note: 'Egg wash on baked goods is easy to miss when you read a menu at a glance. Flag it rather than relying on the pastry description.',
    },
    fish: {
      name: 'Fish',
      short: 'Fillets, anchovies, sauces',
      common: 'Fish fillets, fish fingers, sushi, fish pie, smoked salmon and anchovies.',
      hidden: 'Worcestershire sauce and Caesar dressing (anchovies), fish sauce in Thai and Vietnamese dishes, some pizza and pasta sauces, oyster sauce blends, and stock cubes.',
      note: 'Fish sauce and Worcestershire sauce are the two classic invisible fish sources that slip into otherwise meat or vegetable dishes.',
    },
    peanuts: {
      name: 'Peanuts',
      short: 'Groundnut, satay, arachis oil',
      common: 'Peanut butter, satay sauce, roasted and salted peanuts, groundnut oil, and many Asian and African dishes.',
      hidden: 'Chilli and curry sauces enriched with ground peanuts, chocolate and desserts made on shared lines, and hydrolysed vegetable protein.',
      note: '"Groundnut oil" and "arachis oil" are just peanut oil under another name, and both must be declared. Refined peanut oil is not exempt - only fully refined soybean oil is.',
    },
    soybeans: {
      name: 'Soybeans',
      short: 'Soy sauce, tofu, lecithin',
      common: 'Soy sauce, tofu, edamame, miso, soy milk and tempeh.',
      hidden: 'Many Asian sauces and marinades (hoisin, teriyaki), vegetable oil blends, processed meats with soy filler, some breads, and soy lecithin (E322).',
      note: 'Soy lecithin (E322) is in a huge share of chocolate, spreads and baked goods. Check supplier spec sheets, not just the visible ingredient.',
    },
    milk: {
      name: 'Milk',
      short: 'Cheese, butter, cream, whey',
      common: 'Milk, cheese, butter, cream, yoghurt and ice cream.',
      hidden: 'Milk powder in some breads and doughs, milk protein binders in processed meats, whey seasoning on crisps, sodium caseinate in "non-dairy" toppings, and roux sauces.',
      note: 'Milk protein (casein or whey) turns up in "non-dairy" labelled products and in bread. Never assume plain bread or a dry rub is dairy-free without checking.',
    },
    'tree-nuts': {
      name: 'Tree nuts',
      short: 'Almond, hazelnut, walnut, cashew',
      common: 'Almonds, hazelnuts, walnuts, cashews, pecans, Brazil nuts, pistachios and macadamia - in cakes, pesto, nut oils, nut milks and praline.',
      hidden: 'Pesto and salad dressings, nut oils used for frying or finishing, korma and other sauces thickened with ground nuts, marzipan and frangipane, granola, and nut liqueurs.',
      note: 'Almond milk lattes and pesto pasta are two easy misses. Both hide tree nuts behind a name that does not say "nut".',
    },
    celery: {
      name: 'Celery',
      short: 'Celeriac, stock, seasoning',
      common: 'Celery sticks, celeriac, soups, stews and salads.',
      hidden: 'Stock cubes and bouillon (celery salt is a common base), gravy granules, spice mixes and seasoning salts, some sausages, and ready-made sauces.',
      note: 'Celery salt hides inside almost every commercial stock cube and seasoning blend. Check the base stock before calling any soup or gravy celery-free.',
    },
    mustard: {
      name: 'Mustard',
      short: 'Condiment, dressings, rubs',
      common: 'Mustard condiment, mustard powder, salad dressings and piccalilli.',
      hidden: 'Mayonnaise and many shop-bought sauces, marinades and BBQ rubs, curry powders and spice blends, some breads, and processed meats.',
      note: 'Mustard is a stealth ingredient in mayonnaise and marinades. It rarely appears in the dish name, but it is very often in the sauce.',
    },
    sesame: {
      name: 'Sesame seeds',
      short: 'Buns, tahini, hummus',
      common: 'Sesame seeds on bread and buns, tahini, hummus, halva, sesame oil and falafel.',
      hidden: 'Seeds baked into the underside of buns, spice blends (za’atar, dukkah), tahini-based dressings, and shared fryers with sesame-crumbed items.',
      note: 'Sesame is often baked into the underside of a bun, not just sprinkled on top. A quick glance at the bread can miss it.',
    },
    sulphites: {
      name: 'Sulphites',
      short: 'Wine, dried fruit, vinegar',
      common: 'Wine (especially white and rosé), dried fruit such as apricots and raisins, cordial, fruit juice and cider.',
      hidden: 'Processed meats and sausages, pickled and preserved vegetables, some pre-cut and frozen potato products, vinegar-based sauces, and some soft drinks.',
      note: 'The legal trigger is a concentration above 10 mg/kg or 10 mg/L, not mere presence. Dried apricots and cheap wine are the two most-forgotten sources.',
    },
    lupin: {
      name: 'Lupin',
      short: 'Lupin flour, free-from blends',
      common: 'Lupin flour and lupin seeds (lupini), plus some gluten-free breads and baked goods.',
      hidden: 'Wheat flour blends and baking mixes using lupin as a protein booster, some pasta, vegan meat substitutes, and continental breads and pastries.',
      note: 'Lupin is rare in traditional British cooking but common in continental breads and gluten-free flour blends. Check any imported or "free-from" flour.',
    },
    molluscs: {
      name: 'Molluscs',
      short: 'Mussels, oysters, squid, snails',
      common: 'Mussels, clams, oysters, scallops, squid, octopus and snails (escargot).',
      hidden: 'Oyster sauce in stir-fries and marinades, seafood stock in paella and risotto bases, squid ink colouring pasta or rice, and surimi seafood extenders.',
      note: 'Oyster sauce is the classic hidden mollusc. It turns up in stir-fries and marinades that otherwise look meat or vegetable only.',
    },
  },
};
